// Global Instructions Rule Applied!

import { getBackendBaseUrl } from '../../../core/utils/endpoints.ts'
import { ProspectFields } from './fieldConfigs.ts'
import type { PipelineConfigModel } from '../../../state_manager/data_models/pipeline_config_model.ts'
import type { PipelineStagesModel } from '../../../state_manager/data_models/pipeline_stages_model.ts'

export type StageReset = { configId: string; label: string }

export function normalizeFundIdFromPatch(value: unknown): string | null {
  if (value == null || value === '') return null
  if (typeof value === 'object' && value !== null && 'value' in value) {
    const v = (value as { value?: string }).value
    return v != null && v !== '' ? String(v) : null
  }
  return String(value)
}

/**
 * True when stage id is a visible_tbl config for the given pipeline.
 */
export function isPipelineStageValidForPipeline(
  stageId: string | null | undefined,
  effectivePipelineId: string | null | undefined,
  pipelineConfigs: PipelineConfigModel[]
): boolean {
  if (!stageId || !effectivePipelineId) return false
  return pipelineConfigs.some(
    (c) =>
      c.id === stageId &&
      String(c.pipeline) === String(effectivePipelineId) &&
      c.visible_tbl === true
  )
}

/**
 * Prefer the visible_tbl config with default === true; otherwise the config with the lowest
 * sequence_tbl (missing sequence_tbl sorts last; ties broken by sequence_kb).
 * DB requires pipeline_prospects.stage to be non-null when a reset is applied.
 */
export function getDefaultPipelineConfigForPipeline(
  effectivePipelineId: string | null | undefined,
  pipelineConfigs: PipelineConfigModel[],
  pipelineStages: PipelineStagesModel[]
): StageReset | null {
  if (!effectivePipelineId || !pipelineConfigs.length) return null
  const filtered = pipelineConfigs.filter(
    (c) => String(c.pipeline) === String(effectivePipelineId) && c.visible_tbl === true
  )
  if (!filtered.length) return null
  const defaultRow = filtered.find((c) => c.default === true && c.id)
  if (defaultRow?.id) {
    const stage = pipelineStages.find((s) => s.id === defaultRow.stage)
    return { configId: defaultRow.id, label: stage?.name || '' }
  }
  return getFirstPipelineConfigForPipeline(effectivePipelineId, pipelineConfigs, pipelineStages)
}

/**
 * Fallback when no default-flag row: visible_tbl config with the lowest sequence_tbl; rows without
 * sequence_tbl sort after those with it; ties use sequence_kb, then id.
 */
function getFirstPipelineConfigForPipeline(
  effectivePipelineId: string | null | undefined,
  pipelineConfigs: PipelineConfigModel[],
  pipelineStages: PipelineStagesModel[]
): StageReset | null {
  if (!effectivePipelineId || !pipelineConfigs.length) return null
  const filtered = pipelineConfigs.filter(
    (c) => String(c.pipeline) === String(effectivePipelineId) && c.visible_tbl === true
  )
  if (!filtered.length) return null
  const seqTblKey = (c: PipelineConfigModel) =>
    typeof c.sequence_tbl === 'number' && !Number.isNaN(c.sequence_tbl)
      ? c.sequence_tbl
      : Number.POSITIVE_INFINITY
  const sorted = [...filtered].sort((a, b) => {
    const d = seqTblKey(a) - seqTblKey(b)
    if (d !== 0) return d
    const kb = (a.sequence_kb ?? 0) - (b.sequence_kb ?? 0)
    if (kb !== 0) return kb
    return String(a.id ?? '').localeCompare(String(b.id ?? ''))
  })
  const first = sorted[0]
  if (!first?.id) return null
  const stage = pipelineStages.find((s) => s.id === first.stage)
  return { configId: first.id, label: stage?.name || '' }
}

/**
 * Same resolution as ProspectOverviewSection: fund row → pipeline_origination, else saved field, else default.
 */
export function getEffectiveFundraisingPipelineIdForProspect(
  prospect: Record<string, any>,
  fundOptions: any[],
  defaultFundraisingPipelineId: string | null
): string | null {
  const fundId = prospect[ProspectFields.prospects__fund]
  if (fundId != null && fundId !== '' && Array.isArray(fundOptions)) {
    const fundRow = fundOptions.find(
      (f: { id?: string; value?: string }) => String(f.id ?? f.value) === String(fundId)
    )
    const po = fundRow?.pipeline_origination
    if (typeof po === 'string' && po.length > 0) return po
  }
  const saved = prospect[ProspectFields.prospects__pipeline_origination]
  if (typeof saved === 'string' && saved.length > 0) return saved
  return defaultFundraisingPipelineId
}

/**
 * On fund change: always set pipeline_origination from the fund. Only reset pipeline stage when the
 * current stage is missing or not valid for the fund's effective pipeline; then prefer default-flag
 * config, else lowest sequence_tbl visible_tbl config. Follow-up PATCH to pipeline-prospects only when stage
 * actually resets and a pipeline_prospects row exists.
 */
export function applyFundChangeToUpdates(
  updates: Record<string, any>,
  merged: Record<string, any>,
  ctx: {
    fundOptions: any[]
    pipelineConfigs: PipelineConfigModel[]
    pipelineStages: PipelineStagesModel[]
    defaultFundraisingPipelineId: string | null
  }
): {
  pipelineOrigination: string | null
  stageReset: StageReset | null
  pipelineProspectsRowId: string | undefined | null
  needsPipelineProspectsStagePatch: boolean
} {
  const { fundOptions, pipelineConfigs, pipelineStages, defaultFundraisingPipelineId } = ctx

  const fundId = normalizeFundIdFromPatch(updates[ProspectFields.prospects__fund])
  const fundRow = fundId
    ? fundOptions.find((f: { id?: string; value?: string }) => (f.id ?? f.value) === fundId)
    : undefined
  const poRaw = fundRow?.pipeline_origination
  const pipelineOrigination = typeof poRaw === 'string' && poRaw.length > 0 ? poRaw : null
  updates[ProspectFields.prospects__pipeline_origination] = pipelineOrigination

  const effectivePipeline = pipelineOrigination || defaultFundraisingPipelineId
  const currentStageId = merged[ProspectFields.pipeline_prospects__stage] as string | undefined

  let stageReset: StageReset | null = null

  const stageStillValid =
    effectivePipeline &&
    isPipelineStageValidForPipeline(currentStageId, effectivePipeline, pipelineConfigs)

  if (stageStillValid) {
    const pipelineProspectsRowId = merged['pipeline_prospects__id']
    return {
      pipelineOrigination,
      stageReset: null,
      pipelineProspectsRowId,
      needsPipelineProspectsStagePatch: false
    }
  }

  let resolved =
    effectivePipeline &&
    getDefaultPipelineConfigForPipeline(effectivePipeline, pipelineConfigs, pipelineStages)
  if (!resolved && defaultFundraisingPipelineId) {
    resolved = getDefaultPipelineConfigForPipeline(
      defaultFundraisingPipelineId,
      pipelineConfigs,
      pipelineStages
    )
  }

  if (resolved) {
    stageReset = resolved
    updates[ProspectFields.pipeline_prospects__stage] = resolved.configId
    updates[ProspectFields.pipeline_prospects__stage__label] = resolved.label
    updates['pipeline_config__id'] = resolved.configId
  } else {
    updates[ProspectFields.pipeline_prospects__stage] = ''
    updates[ProspectFields.pipeline_prospects__stage__label] = ''
    updates['pipeline_config__id'] = ''
  }

  const pipelineProspectsRowId = merged['pipeline_prospects__id']
  const needsPipelineProspectsStagePatch = Boolean(stageReset && pipelineProspectsRowId)

  return {
    pipelineOrigination,
    stageReset,
    pipelineProspectsRowId,
    needsPipelineProspectsStagePatch
  }
}

export async function syncFundChangePipelineOnServer(
  recordId: string,
  pipelineOrigination: string | null,
  needsPipelineProspectsStagePatch: boolean,
  stageReset: StageReset | null,
  pipelineProspectsRowId: string | undefined | null,
  getToken: () => string | null
): Promise<void> {
  const currentToken = getToken()
  if (!currentToken) return

  try {
    const prospectsBody: Record<string, string | null> = {
      id: recordId,
      pipeline_origination: pipelineOrigination
    }
    const prospectsRes = await fetch(`${getBackendBaseUrl()}/prospects`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${currentToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(prospectsBody)
    })
    if (!prospectsRes.ok && process.env.NODE_ENV === 'development') {
      console.error('syncFundChangePipelineOnServer: PATCH prospects pipeline_origination failed', prospectsRes.status)
    }

    if (needsPipelineProspectsStagePatch && stageReset && pipelineProspectsRowId) {
      const stageDate = new Date().toISOString().split('T')[0]
      const ppRes = await fetch(`${getBackendBaseUrl()}/pipeline-prospects`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${currentToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: pipelineProspectsRowId,
          stage: stageReset.configId,
          stage_date: stageDate
        })
      })
      if (!ppRes.ok && process.env.NODE_ENV === 'development') {
        console.error('syncFundChangePipelineOnServer: PATCH pipeline-prospects stage reset failed', ppRes.status)
      }
    }
  } catch (err) {
    if (process.env.NODE_ENV === 'development') {
      console.error('syncFundChangePipelineOnServer: follow-up PATCH after fund change failed', err)
    }
  }
}
